export const fmtCurrency = (n: number | string | null | undefined) => {
  const num = Number(n ?? 0);
  return num.toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });
};

export const fmtNumber = (n: number | string | null | undefined, digits = 2) => {
  const num = Number(n ?? 0);
  return num.toFixed(digits);
};

export const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  // For date-only strings (YYYY-MM-DD), parse as local to avoid timezone shift
  const s = String(d).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return s;
  }
  return new Date(d).toLocaleDateString('en-CA');
};

export type DisplayStatus =
  | 'draft'
  | 'pending'
  | 'processing'
  | 'paid'
  | 'closed'
  | 'voided';

/**
 * Derives the user-facing status of a pay period from payment progress and dates,
 * rather than the raw DB status column (which flips to 'paid' on the very first
 * payment). Lifecycle: draft → pending → processing → paid.
 *
 *  - closed / voided  → shown as-is (terminal states)
 *  - paid             → every payable employee is fully paid
 *  - processing       → some money paid out, but not everyone is fully paid
 *  - pending          → the period has ended but nothing has been paid yet
 *  - draft            → the period has not ended yet and nothing has been paid
 */
export const periodDisplayStatus = (args: {
  status: string;
  endDate: string;
  totalNet: number;
  totalPaid: number;
  allPaid: boolean;
}): DisplayStatus => {
  if (args.status === 'closed') return 'closed';
  if (args.status === 'voided') return 'voided';
  if (args.allPaid && args.totalNet > 0) return 'paid';
  if (args.totalPaid > 0) return 'processing';
  const today = new Date().toISOString().slice(0, 10);
  if (args.endDate && String(args.endDate).slice(0, 10) < today) return 'pending';
  return 'draft';
};

/**
 * Convenience wrapper for a period row from the `/periods` list endpoint, which
 * carries the aggregate fields (total_paid / payable_count / fully_paid_count)
 * needed to derive the display status.
 */
export const periodRowDisplayStatus = (p: {
  status: string;
  end_date: string;
  total_net?: number | string;
  total_paid?: number | string;
  payable_count?: number;
  fully_paid_count?: number;
}): DisplayStatus => {
  const payable = Number(p.payable_count || 0);
  return periodDisplayStatus({
    status: p.status,
    endDate: p.end_date,
    totalNet: Number(p.total_net || 0),
    totalPaid: Number(p.total_paid || 0),
    allPaid: payable > 0 && Number(p.fully_paid_count || 0) >= payable,
  });
};
